import { cn } from "@app/lib/utils";
import { isQuestionGenerating } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/services";
import { type QuestionnaireOverviewData } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/types";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon, type IconName } from "@design-system/Icon";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { type ColumnDef } from "@design-system/Table";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import {
    AiAnswerStatusFilter,
    AnswerStatus,
    QuestionType,
} from "@generated/client/graphql";
import { useMemo, useOptimistic, useTransition, type FC } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    useGenerateAiSuggestion,
    useGetDocuments,
    useQuestionAndAnswerUpdate,
} from "../../../../QuestionsTab/data";
import { RESOURCE_NAME, keys } from "../../../i18n";

export const AnswerIcon: FC<{
    name: IconName;
    answerType: "suggestion" | "answer";
    tooltip?: string;
    color?: string;
    borderColor?: string;
}> = ({ name, answerType, tooltip, color, borderColor }) => {
    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <Box
                    className={cn(
                        "grid place-items-center",
                        "size-5",
                        "rounded-lg",
                        borderColor && `border border-${borderColor}`,
                        color ||
                            (answerType === "suggestion"
                                ? "bg-accent-1-50 text-accent-1-300"
                                : "bg-tertiary text-secondary"),
                    )}
                >
                    <Icon name={name} size="sm" />
                </Box>
            </Tooltip.Trigger>
            {tooltip && <Tooltip.Content>{tooltip}</Tooltip.Content>}
        </Tooltip>
    );
};
export const questionTypeToIconName: Record<QuestionType, IconName> = {
    [QuestionType.MultiSelect]: "listChecksMulti",
    [QuestionType.SingleSelect]: "listChecksSingle",
    [QuestionType.Text]: "alignLeft",
    [QuestionType.Value]: "number",
};

export const questionTypeToTooltip: Record<QuestionType, string> = {
    [QuestionType.MultiSelect]: keys.table_question_type_multi_select,
    [QuestionType.SingleSelect]: keys.table_question_type_single_select,
    [QuestionType.Text]: keys.table_question_type_text,
    [QuestionType.Value]: keys.table_question_type_value,
};

export const answerColumn: ColumnDef<QuestionnaireOverviewData[number]> = {
    id: "answer",
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);
        return (
            <Text className="text-secondary" variant="body-sm-bold">
                {t(keys.table_header_answer)}
            </Text>
        );
    },
    cell: function RowCell({ row }) {
        const { t } = useTranslation(RESOURCE_NAME);

        const { answer, evidenceFiles } = useMemo(() => {
            const rawAnswer = row.original.answer?.value ?? null;
            const options = rawAnswer?.options?.map(
                (option: number) => row.original.options?.[option],
            );
            const answer =
                rawAnswer?.text ?? rawAnswer?.num ?? options?.join(", ");
            const evidenceFiles = row.original.answer?.evidenceFiles;

            return { answer, evidenceFiles };
        }, [
            row.original.answer?.value,
            row.original.options,
            row.original.answer?.evidenceFiles,
        ]);

        const handleCopyAnswer = () => {
            navigator.clipboard.writeText(answer);
        };

        return (
            <FlexCol gap="1">
                {answer && (
                    <FlexRow
                        alignItems="center"
                        gap="1"
                        justifyContent="between"
                    >
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <div className="max-h-[3em] flex-1 overflow-hidden">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm]}
                                        className="markdown text-primary break-words [&_p]:m-0 [&_table]:whitespace-nowrap [&:not(table)]:line-clamp-2"
                                    >
                                        {String(answer)}
                                    </ReactMarkdown>
                                </div>
                            </Tooltip.Trigger>
                            <Tooltip.Content className="break-words max-w-[600px]">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    className="markdown text-sm text-white [&_p]:text-white [&_ul]:text-white [&_ol]:text-white [&_li]:text-white [&_table]:text-white [&_th]:text-white [&_td]:text-white break-words"
                                >
                                    {String(answer)}
                                </ReactMarkdown>
                            </Tooltip.Content>
                        </Tooltip>

                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Button
                                    variant="text"
                                    size="sm"
                                    onClick={handleCopyAnswer}
                                >
                                    <Button.Icon name="duplicate" size="sm" />
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.copy_to_clipboard_tooltip)}
                            </Tooltip.Content>
                        </Tooltip>
                    </FlexRow>
                )}
                {evidenceFiles && evidenceFiles?.length > 0 && (
                    <FlexRow
                        alignItems="center"
                        gap="1"
                        className="text-beavrGreen"
                    >
                        <Icon name="document" size="sm" />
                        <Text variant="body-sm-bold">
                            {t(keys.evidence_files, {
                                count: evidenceFiles.length,
                            })}
                        </Text>
                    </FlexRow>
                )}
            </FlexCol>
        );
    },
    meta: {
        cellClassName: "max-w-[300px]",
        headerClassName: "max-w-[300px]",
        colWidth: "300px",
    },
};

export const aiAnswerColumn: ColumnDef<QuestionnaireOverviewData[number]> = {
    id: "aiAnswer",
    header: () => {
        const { t } = useTranslation(RESOURCE_NAME);
        return (
            <Text className="text-secondary" variant="body-sm-bold">
                {t(keys.table_header_ai_answer)}
            </Text>
        );
    },
    filterFn: (row, columnId, filterValue) => {
        if (columnId !== "aiAnswer" || !Array.isArray(filterValue)) {
            return true;
        }
        const rowAiAnswerStatus =
            row.original.answer?.aiSuggestion == null
                ? AiAnswerStatusFilter.NotStarted
                : row.original.answer?.aiSuggestion?.success
                  ? AiAnswerStatusFilter.AiSuggestionSuccess
                  : AiAnswerStatusFilter.AiSuggestionFailed;
        return (
            filterValue.length === 0 ||
            filterValue.includes(row.original.type) ||
            filterValue.includes(rowAiAnswerStatus)
        );
    },
    cell: function RowCell({ row }) {
        const { t } = useTranslation(RESOURCE_NAME);
        const aiSuggestion = row.original.answer?.aiSuggestion ?? null;
        const questionType = row.original.type;

        const suggestionSuccess = aiSuggestion?.success;
        const textSuggestion =
            row?.original?.type === QuestionType.Text ||
            row?.original?.type === QuestionType.Value
                ? aiSuggestion?.value
                : row?.original?.type === QuestionType.SingleSelect
                  ? (row?.original?.options?.find(
                        (_, idx) => idx == aiSuggestion?.value,
                    ) ?? "")
                  : (row?.original?.options
                        ?.filter((_, idx) => aiSuggestion?.value?.includes(idx))
                        ?.map((option) => option)
                        .join(", ") ?? "");

        const isGenerating = isQuestionGenerating(row.original);

        const [isPending, startTransition] = useTransition();
        const [isOptimisticGenerating, setIsOptimisticGenerating] =
            useOptimistic(isGenerating);

        const { generateAiSuggestion, isPending: isGeneratePending } =
            useGenerateAiSuggestion(row.original.id);

        const shouldShowGenerationLoading =
            isOptimisticGenerating || isGeneratePending;

        const {
            icon,
            text,
            textColor,
            showCopy,
            showInsert,
            disableInsert,
            disableGenerate,
        }: {
            icon?: {
                name: IconName;
                answerType: "suggestion" | "answer";
                tooltip?: string;
                color?: string;
                borderColor?: string;
            };
            text?: string;
            textColor?: string;
            showCopy?: boolean;
            showInsert?: boolean;
            disableInsert?: boolean;
            disableGenerate?: boolean;
        } = useMemo(() => {
            if (shouldShowGenerationLoading) {
                return {
                    icon: {
                        name: "loader",
                        answerType: "suggestion",
                        tooltip: t(keys.generating_suggestion),
                        color: "text-accent-1-400 animate-spin",
                        borderColor: "secondary",
                    },
                    text: t(keys.generating_suggestion),
                    textColor: "text-secondary",
                    showCopy: false,
                    showInsert: false,
                    disableInsert: true,
                    disableGenerate: true,
                };
            }

            if (suggestionSuccess && textSuggestion != null) {
                const isValidated =
                    row.original?.answer?.status === AnswerStatus.Validated;
                return {
                    text: textSuggestion,
                    textColor: "text-primary",
                    showCopy: true,
                    showInsert: true,
                    disableInsert: isValidated,
                    disableGenerate: isValidated,
                };
            }

            if (typeof suggestionSuccess === "boolean" && !suggestionSuccess) {
                return {
                    icon: {
                        name: "warning",
                        answerType: "suggestion",
                        tooltip: t(keys.suggestion_error),
                        color: "text-[#DBA600]",
                        borderColor: "warning-600",
                    },
                    text: t(keys.regenerate_answer_button),
                    textColor: "text-secondary",
                    disableInsert: true,
                    disableGenerate: false,
                };
            }

            return {};
        }, [
            suggestionSuccess,
            textSuggestion,
            questionType,
            t,
            isOptimisticGenerating,
            isGenerating,
            isGeneratePending,
            isPending,
        ]);

        const documentCitations =
            row.original.answer?.aiSuggestion?.documentCitations;

        const uniqueDocIdsCount = useMemo(() => {
            const docIds = documentCitations?.map(
                (c: { documentId: string }) => c.documentId,
            );
            return new Set(docIds).size;
        }, [documentCitations]);

        const { documents: suggestedDocuments } = useGetDocuments(
            documentCitations?.map(
                (c: { documentId: string }) => c.documentId,
            ) ?? [],
        );

        const handleSuggestButtonClick = () => {
            startTransition(() => {
                setIsOptimisticGenerating(true);
                generateAiSuggestion(
                    {
                        questionId: row.original.id,
                    },
                    {
                        onError: () => {
                            setIsOptimisticGenerating(false);
                        },
                    },
                );
            });
        };

        const handleCopySuggestion = () => {
            navigator.clipboard.writeText(textSuggestion);
        };

        const {
            updateTextAnswer,
            updateSelectAnswer,
            updateValueAnswer,
            updateLastInsertedDate,
            updateEvidenceFilesAsync,
        } = useQuestionAndAnswerUpdate(
            row.original.id,
            row.original.answer?.id,
            row.original.questionnaireId,
            {
                groupId: row.original.group?.id,
            },
        );

        const handleInsertSuggestion = () => {
            if (row.original.type === QuestionType.Text) {
                updateTextAnswer(textSuggestion ?? "");
            }

            if (row.original.type === QuestionType.Value) {
                updateValueAnswer(textSuggestion ?? "");
            }

            if (row.original.type === QuestionType.MultiSelect) {
                updateSelectAnswer(aiSuggestion?.value ?? []);
            }

            if (row.original.type === QuestionType.SingleSelect) {
                updateSelectAnswer([aiSuggestion?.value ?? ""]);
            }

            if (documentCitations) {
                updateEvidenceFilesAsync({
                    fileIds: suggestedDocuments
                        ?.map(
                            (document) =>
                                document?.lastApprovedVersion?.finalFile?.id,
                        )
                        .filter((fileId) => typeof fileId === "string"),
                });
            }
            updateLastInsertedDate();
        };

        return (
            <FlexCol gap="1">
                {suggestionSuccess != null || shouldShowGenerationLoading ? (
                    <FlexRow
                        alignItems="center"
                        gap="1"
                        justifyContent="between"
                    >
                        {text && (
                            <FlexRow
                                alignItems="center"
                                gap="1.5"
                                className="overflow-hidden"
                            >
                                {icon && (
                                    <Icon
                                        className={icon.color}
                                        size="lg"
                                        {...icon}
                                    />
                                )}
                                {!suggestionSuccess ||
                                shouldShowGenerationLoading ? (
                                    <Ellipsis lineClamp={2} hasTooltip asChild>
                                        <Text className={textColor}>
                                            {text}
                                        </Text>
                                    </Ellipsis>
                                ) : (
                                    <Tooltip>
                                        <Tooltip.Trigger asChild>
                                            <div className="max-h-[3em] flex-1 overflow-hidden">
                                                <ReactMarkdown
                                                    remarkPlugins={[remarkGfm]}
                                                    className="markdown text-primary break-words [&_p]:m-0 [&_table]:whitespace-nowrap [&:not(table)]:line-clamp-2"
                                                >
                                                    {String(text)}
                                                </ReactMarkdown>
                                            </div>
                                        </Tooltip.Trigger>
                                        <Tooltip.Content className="break-words max-w-[600px]">
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                className="markdown text-sm text-white [&_p]:text-white [&_ul]:text-white [&_ol]:text-white [&_li]:text-white [&_table]:text-white [&_th]:text-white [&_td]:text-white break-words"
                                            >
                                                {String(text)}
                                            </ReactMarkdown>
                                        </Tooltip.Content>
                                    </Tooltip>
                                )}
                            </FlexRow>
                        )}
                        <FlexRow alignItems="center" gap="1">
                            {text && (
                                <>
                                    {showCopy && (
                                        <Tooltip>
                                            <Tooltip.Trigger asChild>
                                                <Button
                                                    variant="text"
                                                    size="sm"
                                                    onClick={
                                                        handleCopySuggestion
                                                    }
                                                >
                                                    <Button.Icon
                                                        name="duplicate"
                                                        size="sm"
                                                    />
                                                </Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Content>
                                                {t(
                                                    keys.copy_to_clipboard_tooltip,
                                                )}
                                            </Tooltip.Content>
                                        </Tooltip>
                                    )}
                                    {showInsert && (
                                        <Tooltip>
                                            <Tooltip.Trigger asChild>
                                                <Button
                                                    disabled={disableInsert}
                                                    variant="outlined"
                                                    size="sm"
                                                    onClick={
                                                        handleInsertSuggestion
                                                    }
                                                >
                                                    <Button.Icon
                                                        name="check"
                                                        size="sm"
                                                    />
                                                </Button>
                                            </Tooltip.Trigger>
                                            <Tooltip.Content>
                                                <FlexCol gap="1">
                                                    <Text variant="body-sm-bold">
                                                        {t(
                                                            keys.insert_suggestion_tooltip_title,
                                                        )}
                                                    </Text>
                                                    <ul className="list-disc ml-5">
                                                        <li className="text-white">
                                                            <Text variant="body-sm-bold">
                                                                {t(
                                                                    keys.insert_suggestion_tooltip_replace_answer,
                                                                )}
                                                            </Text>
                                                        </li>
                                                        <li className="text-white">
                                                            <Text variant="body-sm-bold">
                                                                {t(
                                                                    keys.insert_suggestion_tooltip_add_documents,
                                                                )}
                                                            </Text>
                                                        </li>
                                                    </ul>
                                                </FlexCol>
                                            </Tooltip.Content>
                                        </Tooltip>
                                    )}
                                </>
                            )}
                            <Tooltip>
                                <Tooltip.Trigger asChild>
                                    <Button
                                        size="sm"
                                        variant="outlined"
                                        disabled={
                                            isGeneratePending || disableGenerate
                                        }
                                        onClick={handleSuggestButtonClick}
                                    >
                                        <Button.Icon name="arrowCircleRight" />
                                    </Button>
                                </Tooltip.Trigger>
                                <Tooltip.Content>
                                    {t(keys.regenerate_suggestion)}
                                </Tooltip.Content>
                            </Tooltip>
                        </FlexRow>
                    </FlexRow>
                ) : (
                    <FlexRow justifyContent="end">
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <Button
                                    className="w-fit position:right"
                                    size="sm"
                                    variant="flatAccent"
                                    disabled={
                                        isGeneratePending || disableGenerate
                                    }
                                    onClick={handleSuggestButtonClick}
                                >
                                    <Button.Icon name="stars" />
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                {t(keys.generate_suggestion)}
                            </Tooltip.Content>
                        </Tooltip>
                    </FlexRow>
                )}

                {documentCitations && documentCitations?.length > 0 && (
                    <FlexRow
                        alignItems="center"
                        gap="1"
                        className="text-accent-1-300"
                    >
                        <Icon name="document" size="sm" />
                        <Text variant="body-sm-bold">
                            {t(keys.document_citations, {
                                count: uniqueDocIdsCount,
                            })}
                        </Text>
                    </FlexRow>
                )}
            </FlexCol>
        );
    },
    meta: {
        cellClassName: "max-w-[300px]",
        headerClassName: "max-w-[300px]",
        colWidth: "300px",
    },
};
